import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Badge, Alert, ProgressBar, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { PaymentTransaction, GroupCredits, PaymentDetails } from "../services/PackageService";
import { apiGet } from "../services/BaseService";
import { fetchMyGroups } from "../services/UserGroupService";
import { useSelector } from "react-redux";
import { isMobileNumber, formatPhoneNumber, getWhatsAppUrl, getPhoneCallUrl } from "../utils/phoneUtils";

const GroupPackagePage: React.FC = () => {
  const [pendingTransactions, setPendingTransactions] = useState<PaymentTransaction[]>([]);
  const [groupCredits, setGroupCredits] = useState<GroupCredits | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'credits' | 'transactions'>('credits');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'netbanking'>('upi');
  const [adminContact, setAdminContact] = useState({ phone: '', email: '' });
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const navigate = useNavigate();

  const { user } = useSelector((state: any) => state.auth);

  useEffect(() => {
    fetchUserGroups();
  }, [user]);

  useEffect(() => {
    if (userGroups.length > 0) {
      fetchGroupCredits();
      fetchPendingTransactions();
      fetchPaymentDetails();
      fetchAdminContact();
    }
  }, [userGroups]);

  const fetchGroupCredits = async () => {
    try {
      if (userGroups.length === 0) return;
      
      const groupId = userGroups[0]._id; // Use first group
      const data = await apiGet(`/groups/${groupId}/credits`);
      setGroupCredits(data);
    } catch (error) {
      console.error('Error fetching group credits:', error);
    }
  };

  const fetchPendingTransactions = async () => {
    try {
      const response = await apiGet('/payment/requests');
      const transactions = response.requests || response;
      // Filter transactions for user's groups
      const userGroupIds = userGroups.map(g => g._id);
      const userTransactions = transactions.filter((t: any) => 
        userGroupIds.includes(t.groupId?._id) || userGroupIds.includes(t.groupId)
      );
      setPendingTransactions(userTransactions || []);
    } catch (error) {
      console.error('Error fetching pending transactions:', error);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const data = await PackageService.getPaymentDetails();
      setPaymentDetails(data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const data = await fetchMyGroups(1, 100);
      setUserGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const fetchAdminContact = async () => {
    try {
      // Fetch admin contact from environment or API
      setAdminContact({
        phone: process.env.REACT_APP_ADMIN_PHONE || '+919876543210',
        email: process.env.REACT_APP_ADMIN_EMAIL || 'admin@natakiya.com'
      });
    } catch (error) {
      console.error('Error fetching admin contact:', error);
    }
  };

  const handlePayNow = async (transaction: PaymentTransaction) => {
    setSelectedTransaction(transaction);
    if (paymentDetails?.upiId) {
      const upiString = `upi://pay?pa=${paymentDetails.upiId}&pn=Natakiya&am=${transaction.amount}&cu=INR&tn=Package%20Payment`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiString)}`;
      setQrCodeUrl(qrUrl);
    }
    setTransactionId(transaction.transactionId || '');
    setShowPaymentModal(true);
  };

  const submitPayment = async () => {
    if (!selectedTransaction || !transactionId.trim()) {
      alert('Please enter transaction ID');
      return;
    }
    
    try {
      setLoading(true);
      await PackageService.updatePaymentRequest(selectedTransaction._id, {
        transactionId: transactionId.trim(),
        paymentMethod: paymentMethod as 'upi' | 'netbanking'
      });
      
      alert('Payment updated successfully! Admin will verify and approve.');
      setShowPaymentModal(false);
      fetchPendingTransactions();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Error updating payment request');
    } finally {
      setLoading(false);
    }
  };

  // Note: Group admins cannot approve/reject payments - only view status
  // Payment approval is handled by Super Admin in ManagePackagePage

  const getUsageColor = (percent: number) => {
    if (percent >= 90) return 'danger';
    if (percent >= 70) return 'warning';
    return 'success';
  };

  return (
    <PermissionWrapper pageName="GroupPackagePage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-box-seam me-2"></i>
              Group Package Management
            </h2>
            <p className="text-muted">Manage your group's package subscription and credits</p>
          </Col>
        </Row>

        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <div className="btn-group" role="group">
              <Button 
                variant={activeTab === 'credits' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('credits')}
              >
                <i className="bi bi-credit-card me-2"></i>
                Credits & Usage
              </Button>
              <Button 
                variant={activeTab === 'transactions' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('transactions')}
              >
                <i className="bi bi-clock-history me-2"></i>
                Payment Requests ({pendingTransactions.length})
              </Button>
            </div>
          </Col>
        </Row>

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <Row>
            <Col>
              {groupCredits?.hasActivePackage ? (
                <div>
                  {/* Package Overview */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h5>
                        <i className="bi bi-box me-2"></i>
                        Current Package: {groupCredits.packageName}
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <h6>Schedule Credits</h6>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span>Used: {groupCredits.usedCredits.scheduleCredits}</span>
                              <span>Remaining: {groupCredits.remainingCredits.scheduleCredits}</span>
                            </div>
                            <ProgressBar 
                              now={(groupCredits.usedCredits.scheduleCredits / groupCredits.totalCredits.scheduleCredits) * 100}
                              variant={getUsageColor((groupCredits.usedCredits.scheduleCredits / groupCredits.totalCredits.scheduleCredits) * 100)}
                            />
                            <small className="text-muted">
                              Total: {groupCredits.totalCredits.scheduleCredits} schedules
                            </small>
                          </div>
                        </Col>
                        <Col md={6}>
                          <h6>Ticket Credits</h6>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span>Used: {groupCredits.usedCredits.ticketCredits}</span>
                              <span>Remaining: {groupCredits.remainingCredits.ticketCredits}</span>
                            </div>
                            <ProgressBar 
                              now={(groupCredits.usedCredits.ticketCredits / groupCredits.totalCredits.ticketCredits) * 100}
                              variant={getUsageColor((groupCredits.usedCredits.ticketCredits / groupCredits.totalCredits.ticketCredits) * 100)}
                            />
                            <small className="text-muted">
                              Total: {groupCredits.totalCredits.ticketCredits} tickets
                            </small>
                          </div>
                        </Col>
                      </Row>
                      
                      {/* Low Credits Warning */}
                      {(groupCredits.remainingCredits.scheduleCredits <= 2 || 
                        groupCredits.remainingCredits.ticketCredits <= 100) && (
                        <Alert variant="warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>Low Credits Warning:</strong> You're running low on credits. 
                          Consider purchasing additional packages to avoid service interruption.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Quick Actions */}
                  <Card>
                    <Card.Header>
                      <h5>Quick Actions</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-grid gap-2 d-md-flex">
                        <Button 
                          variant="primary"
                          onClick={() => navigate('/dashboard/package-selection')}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Add Credit Package
                        </Button>
                        <Button 
                          variant="outline-primary"
                          onClick={() => navigate('/dashboard/manage-schedule')}
                        >
                          <i className="bi bi-calendar-plus me-2"></i>
                          Create New Schedule
                        </Button>
                        <Button 
                          variant="outline-secondary"
                          onClick={() => navigate('/dashboard/manage-ticket')}
                        >
                          <i className="bi bi-ticket me-2"></i>
                          Manage Tickets
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ) : (
                <Card>
                  <Card.Body className="text-center py-5">
                    <i className="bi bi-box display-1 text-muted"></i>
                    <h4 className="mt-3">No Active Package</h4>
                    <p className="text-muted mb-4">
                      Your group doesn't have an active package subscription. 
                      Purchase a package to start creating schedules and managing tickets.
                    </p>
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={() => navigate('/dashboard/package-selection')}
                    >
                      <i className="bi bi-box-seam me-2"></i>
                      Browse Packages
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5>Payment Requests</h5>
                  <Badge bg="warning" className="fs-6">
                    {pendingTransactions.length} Pending
                  </Badge>
                </Card.Header>
                <Card.Body>
                  {pendingTransactions.length > 0 ? (
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>Package</th>
                          <th>Amount</th>
                          <th>Payment Method</th>
                          <th>Transaction ID</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingTransactions.map((transaction) => (
                          <tr key={transaction._id}>
                            <td>{(transaction as any).packageId?.packageName || 'N/A'}</td>
                            <td>₹{transaction.amount}</td>
                            <td>
                              <Badge bg="info">
                                {transaction.paymentMethod.toUpperCase()}
                              </Badge>
                            </td>
                            <td>{transaction.transactionId || 'N/A'}</td>
                            <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                            <td>
                              <Badge bg="warning">
                                {transaction.status.toUpperCase()}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTransaction(transaction);
                                    setShowTransactionModal(true);
                                  }}
                                >
                                  View
                                </Button>
                                {!transaction.transactionId && (
                                  <Button 
                                    variant="success" 
                                    size="sm"
                                    onClick={() => handlePayNow(transaction)}
                                  >
                                    Pay Now
                                  </Button>
                                )}
                                {/* Communication Buttons */}
                                {isMobileNumber(adminContact.phone) && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    href={getPhoneCallUrl(adminContact.phone)}
                                    title={`Call Admin ${formatPhoneNumber(adminContact.phone)}`}
                                  >
                                    <i className="bi bi-telephone"></i>
                                  </Button>
                                )}
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  href={getWhatsAppUrl(adminContact.phone, `Hi Admin, I need help with my payment request for ${(transaction as any).packageId?.packageName}. Transaction ID: ${transaction._id}`)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`WhatsApp Admin ${formatPhoneNumber(adminContact.phone)}`}
                                >
                                  <i className="bi bi-whatsapp"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-5">
                      <i className="bi bi-credit-card display-1 text-muted mb-3"></i>
                      <h5 className="text-muted mb-3">No Payment Requests</h5>
                      <p className="text-muted mb-4">
                        You don't have any pending payment requests at the moment.
                      </p>
                      <Button 
                        variant="primary"
                        onClick={() => navigate('/dashboard/package-selection')}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Credit Package
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Transaction Details Modal */}
        <Modal show={showTransactionModal} onHide={() => setShowTransactionModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Payment Request Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTransaction && (
              <div>
                <Row className="mb-3">
                  <Col sm={6}>
                    <strong>Package:</strong> {(selectedTransaction as any).packageId?.packageName || 'N/A'}
                  </Col>
                  <Col sm={6}>
                    <strong>Amount:</strong> ₹{selectedTransaction.amount}
                  </Col>
                  <Col sm={6}>
                    <strong>Payment Method:</strong> {selectedTransaction.paymentMethod.toUpperCase()}
                  </Col>
                  <Col sm={6}>
                    <strong>Status:</strong> 
                    <Badge bg="warning" className="ms-2">
                      {selectedTransaction.status.toUpperCase()}
                    </Badge>
                  </Col>
                  <Col sm={6}>
                    <strong>Transaction ID:</strong> {selectedTransaction.transactionId || 'Not provided'}
                  </Col>
                  <Col sm={6}>
                    <strong>Date:</strong> {new Date(selectedTransaction.createdAt).toLocaleString()}
                  </Col>
                </Row>
                
                {selectedTransaction.paymentProof && (
                  <div className="mb-3">
                    <strong>Payment Proof:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {selectedTransaction.paymentProof}
                    </div>
                  </div>
                )}
                
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  This payment request is pending admin approval. You will be notified once it's processed.
                </Alert>
                
                {/* Admin Contact Section */}
                <Card className="border-0" style={{ backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
                  <Card.Body>
                    <h6 style={{ color: '#810081' }} className="mb-3">Need Help? Contact Admin</h6>
                    <div className="d-flex gap-2 justify-content-center">
                      {isMobileNumber(adminContact.phone) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={getPhoneCallUrl(adminContact.phone)}
                          className="d-flex align-items-center"
                        >
                          <i className="bi bi-telephone me-1"></i>
                          Call Admin
                        </Button>
                      )}
                      <Button
                        variant="outline-success"
                        size="sm"
                        href={getWhatsAppUrl(adminContact.phone, `Hi Admin, I need help with my payment request for ${(selectedTransaction as any)?.packageId?.packageName}. Transaction ID: ${selectedTransaction?._id}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-whatsapp me-1"></i>
                        WhatsApp Admin
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        href={`mailto:${adminContact.email}?subject=Payment Request Help&body=Hi Admin, I need help with my payment request for ${(selectedTransaction as any)?.packageId?.packageName}. Transaction ID: ${selectedTransaction?._id}`}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-envelope me-1"></i>
                        Email Admin
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTransactionModal(false)}>
              Close
            </Button>
            {selectedTransaction && !selectedTransaction.transactionId && (
              <Button 
                variant="success" 
                onClick={() => {
                  setShowTransactionModal(false);
                  handlePayNow(selectedTransaction);
                }}
              >
                Pay Now
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Payment Modal */}
        <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Complete Payment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTransaction && (
              <div>
                <Card className="mb-3">
                  <Card.Body>
                    <h5>{(selectedTransaction as any).packageId?.packageName}</h5>
                    <h5 className="text-primary">Amount: ₹{selectedTransaction.amount}</h5>
                  </Card.Body>
                </Card>

                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      label="UPI Payment"
                      name="paymentMethod"
                      value="upi"
                      checked={paymentMethod === 'upi'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'upi' | 'netbanking')}
                    />
                    <Form.Check
                      type="radio"
                      label="Net Banking"
                      name="paymentMethod"
                      value="netbanking"
                      checked={paymentMethod === 'netbanking'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'upi' | 'netbanking')}
                    />
                  </div>
                </Form.Group>

                {paymentMethod === 'upi' && paymentDetails && (
                  <Card className="mb-3">
                    <Card.Header><h6>UPI Payment</h6></Card.Header>
                    <Card.Body className="text-center">
                      {qrCodeUrl && (
                        <div className="mb-3">
                          <img src={qrCodeUrl} alt="UPI QR Code" className="img-fluid" style={{maxWidth: '200px'}} />
                          <p className="mt-2">Scan QR code with any UPI app</p>
                        </div>
                      )}
                      <p><strong>UPI ID:</strong> {paymentDetails.upiId}</p>
                      <p><strong>Amount:</strong> ₹{selectedTransaction.amount}</p>
                    </Card.Body>
                  </Card>
                )}

                {paymentMethod === 'netbanking' && paymentDetails && (
                  <Card className="mb-3">
                    <Card.Header><h6>Net Banking Details</h6></Card.Header>
                    <Card.Body>
                      <p><strong>Bank:</strong> {paymentDetails.bankName || 'Not available'}</p>
                      <p><strong>Account Number:</strong> {paymentDetails.accountNumber || 'Not available'}</p>
                      <p><strong>IFSC Code:</strong> {paymentDetails.ifscCode || 'Not available'}</p>
                      <p><strong>Account Holder:</strong> {paymentDetails.accountHolderName || 'Not available'}</p>
                      <p><strong>Amount:</strong> ₹{selectedTransaction.amount}</p>
                    </Card.Body>
                  </Card>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Transaction ID / Reference Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter transaction ID after payment"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Complete the payment and enter the transaction ID here
                  </Form.Text>
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={submitPayment}
              disabled={loading || !transactionId.trim()}
            >
              {loading ? 'Updating...' : 'Update Payment'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default GroupPackagePage;